import type { DashboardSnapshot, FamilyLocation, FamilyProfile, GrandpaCalls, GroupMeetup, JoinFamilyResponse } from '../types';

const apiUrl = import.meta.env.VITE_API_URL?.trim().replace(/\/$/, '');
const TOKEN_KEY = 'locus-device-token-v1';
const PROFILE_KEY = 'locus-profile-id-v1';
const CACHE_KEY = 'locus-dashboard-cache-v2';
const ETAG_KEY = 'locus-dashboard-etag-v2';

export const isApiConfigured = Boolean(apiUrl);

function getDeviceToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function getCachedState(): DashboardSnapshot | null {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    return stored ? JSON.parse(stored) as DashboardSnapshot : null;
  } catch {
    return null;
  }
}

function restoreCachedState(cached: DashboardSnapshot): DashboardSnapshot {
  if (cached.currentProfileId) localStorage.setItem(PROFILE_KEY, cached.currentProfileId);
  return cached;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!apiUrl) throw new Error('Backend L.O.C.U.S. non configurato.');
  const token = getDeviceToken();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(`${apiUrl}${path}`, { ...init, headers });
  const body = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(body.error || 'Errore di comunicazione con L.O.C.U.S.');
  return body as T;
}

export async function joinFamily(details: { displayName: string; familyCode: string; avatarDataUrl?: string }): Promise<JoinFamilyResponse> {
  const result = await request<JoinFamilyResponse>('/api/join', {
    method: 'POST',
    body: JSON.stringify(details),
  });
  localStorage.setItem(TOKEN_KEY, result.deviceToken);
  localStorage.setItem(PROFILE_KEY, result.profile.id);
  localStorage.removeItem(ETAG_KEY);
  return result;
}

export async function getDashboardState(): Promise<DashboardSnapshot> {
  if (!apiUrl) throw new Error('Backend L.O.C.U.S. non configurato.');
  const token = getDeviceToken();
  if (!token) throw new Error('AUTH_REQUIRED');
  const headers = new Headers({ Authorization: `Bearer ${token}` });
  const etag = localStorage.getItem(ETAG_KEY);
  if (etag) headers.set('If-None-Match', etag);
  let response: Response;
  try {
    response = await fetch(`${apiUrl}/api/state`, { headers });
  } catch (error) {
    const cached = getCachedState();
    if (cached && (cached.currentProfileId || localStorage.getItem(PROFILE_KEY))) return restoreCachedState(cached);
    throw error;
  }
  if (response.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
    throw new Error('AUTH_REQUIRED');
  }
  if (response.status === 304) {
    const cached = getCachedState();
    if (cached && (cached.currentProfileId || localStorage.getItem(PROFILE_KEY))) return restoreCachedState(cached);
    localStorage.removeItem(ETAG_KEY);
    response = await fetch(`${apiUrl}/api/state`, {
      headers: new Headers({ Authorization: `Bearer ${token}` }),
    });
  }
  if (response.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
    throw new Error('AUTH_REQUIRED');
  }
  const body = await response.json().catch(() => ({})) as DashboardSnapshot & { error?: string };
  if (!response.ok) throw new Error(body.error || 'Stato non disponibile.');
  const snapshot: DashboardSnapshot = {
    currentProfileId: body.currentProfileId,
    profiles: body.profiles,
    locations: body.locations,
    groupMeetup: body.groupMeetup ?? null,
    grandpaCalls: body.grandpaCalls,
    cachedAt: new Date().toISOString(),
  };
  if (body.currentProfileId) localStorage.setItem(PROFILE_KEY, body.currentProfileId);
  localStorage.setItem(CACHE_KEY, JSON.stringify(snapshot));
  const nextEtag = response.headers.get('ETag');
  if (nextEtag) localStorage.setItem(ETAG_KEY, nextEtag);
  return snapshot;
}

export function getCurrentProfileId(): string | null {
  return localStorage.getItem(PROFILE_KEY);
}

export async function setPresence(profileId: string, locationId: string): Promise<void> {
  await request('/api/presence', { method: 'POST', body: JSON.stringify({ profileId, locationId }) });
  localStorage.removeItem(ETAG_KEY);
}

export async function saveQuickNote(noteText: string): Promise<void> {
  await request('/api/profile/note', { method: 'POST', body: JSON.stringify({ noteText }) });
  localStorage.removeItem(ETAG_KEY);
}

export async function createGroupMeetup(locationId: string, scheduledFor: string): Promise<GroupMeetup> {
  const result = await request<{ meetup: GroupMeetup }>('/api/meetup', {
    method: 'POST',
    body: JSON.stringify({ locationId, scheduledFor }),
  });
  localStorage.removeItem(ETAG_KEY);
  return result.meetup;
}

export async function cancelGroupMeetup(): Promise<void> {
  await request('/api/meetup/cancel', { method: 'POST', body: '{}' });
  localStorage.removeItem(ETAG_KEY);
}

export async function updateGrandpaCalls(action: 'increment' | 'set', count?: number): Promise<GrandpaCalls> {
  const result = await request<{ grandpaCalls: GrandpaCalls }>('/api/grandpa-calls', {
    method: 'POST',
    body: JSON.stringify({ action, count }),
  });
  localStorage.removeItem(ETAG_KEY);
  return result.grandpaCalls;
}

export async function addLocation(details: { name: string; emoji: string; slug: string }): Promise<FamilyLocation> {
  const result = await request<{ location: FamilyLocation }>('/api/locations', { method: 'POST', body: JSON.stringify(details) });
  localStorage.removeItem(ETAG_KEY);
  return result.location;
}

export async function savePushSubscription(profileId: string, subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await request('/api/push-subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      profileId,
      endpoint: subscription.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      userAgent: navigator.userAgent,
    }),
  });
}

export function avatarMap(profiles: FamilyProfile[]): Record<string, string> {
  return Object.fromEntries(profiles.map((profile) => [profile.id, profile.avatar_url || '']));
}
